import React, { useEffect, useState } from 'react';
import { paymentAPI } from '../../api';
import qrImage from '../../assets/qr.jpeg';

const UserSendMoney = () => {
  const [formData, setFormData] = useState({
    amount: '',
    upiId: '',
    transactionId: ''
  });
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [myPayments, setMyPayments] = useState([]);

  useEffect(() => {
    fetchMyPayments();
  }, []);

  const fetchMyPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentAPI.getMyPayments();
      setMyPayments(response?.data?.data || []);
    } catch (fetchError) {
      console.error('Failed to fetch payment history:', fetchError);
      setMyPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const uploadScreenshotToCloudinary = async (file) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dkhwthjov';
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'shreebakers';

    if (!cloudName || !uploadPreset) {
      throw new Error('Cloudinary environment variables are missing');
    }

    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', uploadPreset);

    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
      method: 'POST',
      body: data
    });

    const uploaded = await response.json();

    if (!response.ok || !uploaded?.secure_url) {
      throw new Error(uploaded?.error?.message || 'Cloudinary upload failed');
    }

    return {
      screenshotUrl: uploaded.secure_url,
      screenshotPublicId: uploaded.public_id || null
    };
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setScreenshotFile(null);
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file only.');
      setScreenshotFile(null);
      return;
    }

    setError('');
    setScreenshotFile(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!formData.amount || Number(formData.amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    if (!formData.upiId.trim()) {
      setError('UPI ID is required.');
      return;
    }

    if (!screenshotFile) {
      setError('Please upload payment screenshot.');
      return;
    }

    try {
      setSubmitting(true);

      const uploaded = await uploadScreenshotToCloudinary(screenshotFile);

      await paymentAPI.submitPaymentProof({
        amount: Number(formData.amount),
        upiId: formData.upiId.trim(),
        transactionId: formData.transactionId?.trim() || '',
        screenshotUrl: uploaded.screenshotUrl,
        screenshotPublicId: uploaded.screenshotPublicId
      });

      setMessage('Payment proof submitted successfully.');
      setFormData({ amount: '', upiId: '', transactionId: '' });
      setScreenshotFile(null);
      await fetchMyPayments();
    } catch (submitError) {
      console.error('Submit payment proof error:', submitError);
      setError(submitError?.response?.data?.error || submitError.message || 'Failed to submit payment proof.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-2xl font-bold text-[#0F2F3D] mb-2">Send Money to Desi Mart</h2>
        <p className="text-gray-600">Scan the QR, complete UPI payment, then upload screenshot proof.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-[#0F2F3D] mb-4">Admin QR</h3>
          <div className="border rounded-lg p-4 flex items-center justify-center bg-gray-50">
            <img src={qrImage} alt="Admin payment QR" className="max-w-full h-auto max-h-96 rounded-md" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold text-[#0F2F3D] mb-4">Submit Payment Proof</h3>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              {message}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
              <input
                type="number"
                name="amount"
                min="1"
                value={formData.amount}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                placeholder="Enter paid amount"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UPI ID Used for Payment *</label>
              <input
                type="text"
                name="upiId"
                value={formData.upiId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                placeholder="example@upi"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Transaction ID (Optional)</label>
              <input
                type="text"
                name="transactionId"
                value={formData.transactionId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E4572E]"
                placeholder="Enter UPI transaction ID"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Payment Screenshot *</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
              {screenshotFile && (
                <p className="mt-1 text-xs text-gray-500">Selected: {screenshotFile.name}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 bg-[#E4572E] text-white rounded-lg hover:bg-[#cf4521] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'Uploading & Submitting...' : 'Submit Payment Proof'}
            </button>
          </form>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#0F2F3D]">My Payment History</h3>
          <button
            onClick={fetchMyPayments}
            className="px-3 py-1.5 text-sm rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
            disabled={loading}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-gray-500 text-sm">Loading payment history...</p>
        ) : myPayments.length === 0 ? (
          <p className="text-gray-500 text-sm">No payment submissions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">UPI ID</th>
                  <th className="text-left px-3 py-2">Transaction ID</th>
                  <th className="text-left px-3 py-2">Screenshot</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {myPayments.map((item) => (
                  <tr key={item._id} className="border-b">
                    <td className="px-3 py-2">{new Date(item.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2">₹{Number(item.amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2">{item.upiId}</td>
                    <td className="px-3 py-2">{item.transactionId || '-'}</td>
                    <td className="px-3 py-2">
                      <a
                        href={item.screenshotUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.status === 'VERIFIED'
                          ? 'bg-green-100 text-green-800'
                          : item.status === 'REJECTED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {item.status === 'VERIFIED' ? '✓ Approved' : item.status === 'REJECTED' ? '✗ Rejected' : item.status || 'SUBMITTED'}
                      </span>
                      {item.adminNote && item.status === 'REJECTED' && (
                        <div className="text-xs text-red-500 mt-1">{item.adminNote}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSendMoney;
